/**
 * @fileoverview Work Order Zustand store
 *
 * Manages client-side state for work orders, including
 * the active list, filters, selected WO, stats, and vendors.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { create } from "zustand";
import type {
	WorkOrder,
	WorkOrderStats,
	Vendor,
	WorkOrderStatus,
	WorkOrderType,
	WorkOrderPriority,
} from "@/services/work-order-service";

export interface WOFilters {
	status?: WorkOrderStatus;
	priority?: WorkOrderPriority;
	workType?: WorkOrderType;
	locationId?: number;
	assetId?: number;
	assignedToUserId?: number;
	source?: string;
	overdue?: boolean;
	search?: string;
	page: number;
	limit: number;
	sortBy: string;
	sortOrder: "asc" | "desc";
}

interface WorkOrderState {
	// List
	workOrders: WorkOrder[];
	total: number;
	filters: WOFilters;
	loading: boolean;

	// Selected detail
	selectedWorkOrder: WorkOrder | null;
	detailLoading: boolean;

	// Stats
	stats: WorkOrderStats | null;
	statsLoading: boolean;

	// Vendors cache
	vendors: Vendor[];
	vendorsLoaded: boolean;

	// Actions
	setWorkOrders: (workOrders: WorkOrder[], total: number) => void;
	setFilters: (filters: Partial<WOFilters>) => void;
	resetFilters: () => void;
	setLoading: (loading: boolean) => void;
	setSelectedWorkOrder: (wo: WorkOrder | null) => void;
	setDetailLoading: (loading: boolean) => void;
	setStats: (stats: WorkOrderStats | null) => void;
	setStatsLoading: (loading: boolean) => void;
	setVendors: (vendors: Vendor[]) => void;

	// Optimistic updates
	updateWorkOrderInList: (id: number, updates: Partial<WorkOrder>) => void;
	removeWorkOrderFromList: (id: number) => void;
	addWorkOrderToList: (wo: WorkOrder) => void;
}

const DEFAULT_FILTERS: WOFilters = {
	page: 1,
	limit: 25,
	sortBy: "created_at",
	sortOrder: "desc",
};

export const useWorkOrderStore = create<WorkOrderState>((set) => ({
	workOrders: [],
	total: 0,
	filters: { ...DEFAULT_FILTERS },
	loading: false,

	selectedWorkOrder: null,
	detailLoading: false,

	stats: null,
	statsLoading: false,

	vendors: [],
	vendorsLoaded: false,

	setWorkOrders: (workOrders, total) => set({ workOrders, total }),
	setFilters: (partial) =>
		set((s) => ({ filters: { ...s.filters, ...partial } })),
	resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),
	setLoading: (loading) => set({ loading }),
	setSelectedWorkOrder: (wo) => set({ selectedWorkOrder: wo }),
	setDetailLoading: (loading) => set({ detailLoading: loading }),
	setStats: (stats) => set({ stats }),
	setStatsLoading: (loading) => set({ statsLoading: loading }),
	setVendors: (vendors) => set({ vendors, vendorsLoaded: true }),

	updateWorkOrderInList: (id, updates) =>
		set((s) => ({
			workOrders: s.workOrders.map((wo) =>
				wo.id === id ? { ...wo, ...updates } : wo,
			),
			selectedWorkOrder:
				s.selectedWorkOrder?.id === id
					? { ...s.selectedWorkOrder, ...updates }
					: s.selectedWorkOrder,
		})),

	removeWorkOrderFromList: (id) =>
		set((s) => ({
			workOrders: s.workOrders.filter((wo) => wo.id !== id),
			total: s.total - 1,
		})),

	addWorkOrderToList: (wo) =>
		set((s) => ({
			workOrders: [wo, ...s.workOrders],
			total: s.total + 1,
		})),
}));
